import { LineChart, type DataSeries } from "@/components/charts/LineChart";
import { SeriesLegend } from "@/components/charts/SeriesLegend";
import AdaptationTag from "@/components/display/AdaptationTag";
import { HazardPlusTotal } from "@/features/pe_aldd";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { AnaHazardId } from "@/store/services/facades/types";
import { Rcp, RCP_COLORS } from "@/types/rcp";
import { useTranslation } from "react-i18next";
import { useCollectionState } from "../../context/CollectionContext";
import { CompareResultsMode } from "../../types";
import { ChartDataPoint } from "./common/types";
import { formatMetricValue, formatYAxisTick, MetricKey } from "./common/utils";
const ADAPTED_SUFFIX = "_adapted";
interface ScenarioLegendContentProps {
  series: DataSeries[];
  hazard: HazardPlusTotal;
  hazardName: string;
  isComparison: boolean;
  selectedScenario: Rcp;
  onScenarioSelect: (scenario: Rcp) => void;
}
const ScenarioLegendContent = ({
  series,
  isComparison,
  selectedScenario,
  onScenarioSelect
}: ScenarioLegendContentProps) => {
  const seriesToShow = series.filter(s => isComparison ? s.key.endsWith(ADAPTED_SUFFIX) : true);
  const {
    t
  } = useTranslation("terms");
  return <SeriesLegend className="pb-1 pr-14 @2xl/wrapper:text-sm" items={[...seriesToShow].sort((a, b) => b.key.localeCompare(a.key)).map(item => ({
    key: item.key.replace(ADAPTED_SUFFIX, ""),
    name: item.name ?? item.key,
    color: item.color
  }))} selectedKey={selectedScenario} onSelect={key => onScenarioSelect(key as Rcp)} data-sentry-element="SeriesLegend" data-sentry-component="ScenarioLegendContent" data-sentry-source-file="ScenarioLineChart.tsx">
      {isComparison && <div className="ml-2 flex items-center gap-3 text-gray-500">
          <div className="flex items-center gap-1">
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="2.5" />
            </svg>
            <span>{t("adaptationTypes.baseline")}</span>
          </div>
          <div className="flex items-center gap-1">
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 2" />
            </svg>
            <span>{t("adaptationTypes.enhanced_resilience")}</span>
          </div>
        </div>}
    </SeriesLegend>;
};
interface ScenarioLineChartProps {
  metric: MetricKey;
  title: string;
  description: string;
  chartData: ChartDataPoint[];
  scenarios: Rcp[];
  yAxisUnit: "%" | "$";
  metricLabel?: string;
  adaptedChartData?: ChartDataPoint[];
  compareResultsMode?: CompareResultsMode;
  canRunAutoAdaptation: boolean;
  unavailableMessage?: string;
}
export const ScenarioLineChart = ({
  metric,
  chartData,
  scenarios,
  yAxisUnit,
  metricLabel,
  adaptedChartData,
  compareResultsMode,
  canRunAutoAdaptation,
  unavailableMessage
}: ScenarioLineChartProps) => {
  const {
    rcp,
    setRcp,
    hazard,
    year,
    setYear
  } = useCollectionState();
  const {
    t
  } = useTranslation("terms");
  const isComparison = !!adaptedChartData;
  const hazardName = hazard === "total" ? "All Hazards" : Object.hasOwn(anaHazards, hazard) ? anaHazards[hazard as AnaHazardId].name : hazard;
  const mergedChartData: Record<string, unknown>[] = isComparison ? chartData.map((point, i) => {
    const adaptedPoint = adaptedChartData![i];
    const merged: Record<string, unknown> = {
      ...point
    };
    if (adaptedPoint) {
      Object.entries(adaptedPoint).forEach(([key, value]) => {
        if (key !== "year") merged[`${key}${ADAPTED_SUFFIX}`] = value;
      });
    }
    return merged;
  }) : chartData as Record<string, unknown>[];
  const series: DataSeries[] = isComparison ? scenarios.flatMap(scenario => [{
    key: `${scenario}${ADAPTED_SUFFIX}`,
    color: `${RCP_COLORS[scenario]}`,
    name: t(`scenarios.${scenario}.short`) ?? scenario,
    strokeDasharray: "4 2"
  }, {
    key: scenario,
    color: RCP_COLORS[scenario],
    name: t(`scenarios.${scenario}.short`) ?? scenario,
    alwaysSolid: true
  }]) : scenarios.map(scenario => ({
    key: scenario,
    color: RCP_COLORS[scenario],
    name: t(`scenarios.${scenario}.short`) as string ?? scenario,
    alwaysSolid: compareResultsMode === "baseline",
    strokeDasharray: compareResultsMode === "enhanced_resilience" ? "4 2" : undefined
  }));
  const selectedDataKeys = isComparison ? [rcp, `${rcp}${ADAPTED_SUFFIX}`] : [rcp];
  return <LineChart formatTick={value => formatYAxisTick(metric, value)} chartData={mergedChartData} series={series} formatValue={value => formatMetricValue(yAxisUnit, value, yAxisUnit === "%" ? 4 : 2)} metricLabel={metricLabel} selectedDataKeys={selectedDataKeys} selectedYear={year} onYearChange={setYear} legendContent={props => <ScenarioLegendContent {...props} hazard={hazard} hazardName={hazardName} isComparison={isComparison} selectedScenario={rcp} onScenarioSelect={setRcp} />} columnKeySuffix={isComparison ? ADAPTED_SUFFIX : undefined} columnLabels={isComparison ? [<AdaptationTag key="baseline" type="baseline" />, <AdaptationTag key="enhanced_resilience" type="enhanced_resilience" />] : undefined} tag={canRunAutoAdaptation && compareResultsMode && <AdaptationTag type={compareResultsMode} />} emptyMessage={unavailableMessage} data-sentry-element="LineChart" data-sentry-component="ScenarioLineChart" data-sentry-source-file="ScenarioLineChart.tsx" />;
};